import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTracking } from "../hooks/useTracking";
import { ActionCategory } from "../services/logging/logService";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import Select from "../components/Select";
import BeatLoader from "react-spinners/BeatLoader";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { handleLogout } from "../config/axios";
import { SelectOption } from "../types/documents";
import decryptData from "../decrypt";
import { fetchUserInfo, fetchClientsByAgentCodes, hasMultipleCodes, splitAgentCodes, processAgentCodeValue } from "../services";

// Define types
type Agent = {
  value: string;
  label: string;
};

type Filter = {
  searchFilters: any[];
};

type OrderByColumn = {
  column: string;
  label: string;
  mode: "ASC" | "DESC";
  opened: boolean;
};

type PaginationInfo = {
  currentPage: number;
  pageSize: number;
  hasMorePages: boolean;
};

/**
 * Funzione per registrare direttamente le azioni di ricerca senza usare il sistema di debounce
 */
const logSearchDirectly = (action: string, description: string, metadata: any) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No auth token found, cannot log search');
      return;
    }
    
    // Crea un payload di log simile a quello usato dal logService, ma con ID univoco
    const logMessage = {
      action: `search:${action}`,
      description,
      timestamp: new Date().toISOString(),
      pagePath: window.location.pathname,
      sessionId: `direct_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        ...metadata,
        directLog: true,
        timestamp: new Date().toISOString()
      }
    };
    
    // Payload completo per l'API
    const payload = {
      level: 'info',
      serviceName: 'cediFE',
      message: logMessage,
    };
    
    // Chiamata diretta all'API senza debounce
    axios.post('/nodeapi/writeLog', payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then(() => {
      console.log(`Log di ricerca inviato direttamente: ${action}`);
    })
    .catch(error => {
      console.error('Errore nell\'invio diretto del log:', error);
    });
  } catch (error) {
    console.error('Errore nella preparazione del log diretto:', error);
  }
};

export default function FidoClienti() {
  // Track user actions
  const tracking = useTracking();
  const formikRef = useRef<any>(null);
  const initialLoadDone = useRef(false);
  const [filters, setFilters] = useState<Filter>({ searchFilters: [] });
  const [page, setPage] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [explicit, setExplicit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [highestPageSeen, setHighestPageSeen] = useState(0);
  const [columns, setColumns] = useState<string[]>([]);
  const [orderByColumns, setOrderByColumns] = useState<OrderByColumn[]>([]);
  const [userRole, setUserRole] = useState("");
  const [agentFilters, setAgentFilters] = useState<Agent[]>([]);
  const [theme, setTheme] = useState("light");
  const [currentAgentId, setCurrentAgentId] = useState<string>("");
  const [authorizedAgentIds, setAuthorizedAgentIds] = useState<string[]>([]);
  const [agentiOptions, setAgentiOptions] = useState<SelectOption[]>([]);
  const tableRef = useRef(null);
  const currentTheme = useSelector((state: any) => state.menu.theme);

  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(() => {
    const savedFilters = sessionStorage.getItem("fido-filters");
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      return parsedFilters.agenti || null;
    }
    return null;
  });

  const navigate = useNavigate();

  // New function to fetch user data using userService
  const fetchUserData = async (): Promise<void> => {
    try {
      // Use our service to fetch user information
      const { user, isSupervisor, agentCodes, agents } = await fetchUserInfo();
      
      if (!user) {
        console.error("Unable to fetch user info");
        return;
      }
      
      // Set user role
      const role = isSupervisor ? "supervisor" : "agent";
      setUserRole(role);
      
      // Set current agent ID (first code if multiple)
      const currentAgentCode = agentCodes.length > 0 ? agentCodes[0] : "";
      if (currentAgentCode) {
        setCurrentAgentId(currentAgentCode);
      }
      
      // Format agents for the component
      let formattedAgents: SelectOption[] = [];
      
      if (isSupervisor && agents.length > 0) {
        // For supervisors: all agents from group
        formattedAgents = agents.map((agent) => ({
          value: agent.db2_user_id,
          label: `${agent.first_name} ${agent.last_name}`,
          db2_user_id: agent.db2_user_id,
        }));
      } else if (currentAgentCode) {
        // For regular agents: only their own code
        formattedAgents = [
          {
            value: currentAgentCode,
            label: `${user.firstName} ${user.lastName}`,
            db2_user_id: currentAgentCode,
          },
        ];
      }
      
      // Define authorized IDs for API requests
      let authorizedIds: string[] = [];
      if (role === "supervisor") {
        // For supervisors: process all agent codes, splitting any comma-separated values
        authorizedIds = agents.flatMap(agent => {
          if (hasMultipleCodes(agent.db2_user_id)) {
            return splitAgentCodes(agent.db2_user_id);
          }
          return [agent.db2_user_id];
        }).filter(Boolean);
      } else {
        // For agents: use all their codes
        authorizedIds = agentCodes;
      }
      
      setAuthorizedAgentIds(authorizedIds);
      setAgentiOptions(formattedAgents);
      setAgentFilters(formattedAgents);
      
      console.log("User role:", role);
      console.log("Current agent ID:", currentAgentCode);
      console.log("Authorized agent IDs:", authorizedIds);
      console.log("Agent options:", formattedAgents);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const fetchFilters = async () => {
    setLoading(true);

    try {
      // Skip if no agent IDs available
      if (!authorizedAgentIds.length) {
        console.warn("No agent IDs available for client search");
        return;
      }

      // Use the service to fetch clients, which properly handles comma-separated agent codes
      const clients = await fetchClientsByAgentCodes(authorizedAgentIds);

      // Process the result
      if (Array.isArray(clients)) {
        const searchFilters = clients.map((item: any) => ({
          codice_cliente: item.CODICE_CLIENTE,
          ragione_sociale: item.RAGIONE_SOCIALE,
        }));
        setFilters({ searchFilters });
      } else {
        setFilters({ searchFilters: [] });
      }

      const visibleColumns = ["codice_cliente", "ragione_sociale"];
      setColumns(visibleColumns);
      setLoaded(true);
    } catch (e) {
      console.error("Error fetching filters:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("Formik values aggiornati:", formikRef.current?.values);
  }, [formikRef.current?.values]);

  // Modified fetchOrders function to use the new pagination format
  const fetchOrders = async (filters: any = {}, currentPage: number = page) => {
    try {
      setLoading(true);
  
      // Process agent codes for API request
      let agentCodesForRequest: string[] = [];
      
      if (selectedAgent?.value) {
        // If a specific agent is selected, check if it has multiple codes
        if (hasMultipleCodes(selectedAgent.value)) {
          agentCodesForRequest = splitAgentCodes(selectedAgent.value);
        } else {
          agentCodesForRequest = [selectedAgent.value];
        }
      } else {
        // Otherwise use all authorized agent IDs
        agentCodesForRequest = authorizedAgentIds;
      }
  
      const requestData: any = {
        page: filters.forcePageReset ? 0 : currentPage,
        pageSize: 100,
        agenti: agentCodesForRequest,
        codice_cliente: filters.codice_cliente || "",
        campo_ordinamento: filters.campo_ordinamento || "",
        tipo_ordinamento: filters.tipo_ordinamento || "ASC"
      };
  
      console.log("Request body for situazione_finanziaria_pagina:", requestData);
  
      const response = await fetch(`/nodeapi/situazione_finanziaria_pagina`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify(requestData)
      });
  
       if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/situazione_finanziaria_pagina", JSON.stringify(requestData));
         return;
       }
  
      const result = await response.json();
      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);
  
      if (response.ok && decryptedJson.status === "success" && Array.isArray(decryptedJson.data)) {
        setData(decryptedJson.data);
        
        // Handle the new pagination format
        if (decryptedJson.pagination) {
          const paginationInfo = decryptedJson.pagination as PaginationInfo;
          setHasMorePages(paginationInfo.hasMorePages);
          
          // Update highestPageSeen if we're on a new page and there are more pages
          if (currentPage > highestPageSeen && paginationInfo.hasMorePages) {
            setHighestPageSeen(currentPage);
          }
        } else {
          setHasMorePages(false);
        }
  
        setLoaded(true);
      } else {
        setData([]);
        setHasMorePages(false);
      }
    } catch (e) {
      console.error('Error in fetchOrders:', e);
    } finally {
      setLoading(false);
      setExplicit(false);
    }
  };
  



  useEffect(() => {
    if (authorizedAgentIds.length > 0 && !initialLoadDone.current) {
      fetchFilters();
      
      // Now that we have agent IDs, check for saved filters and load data
      const savedFilters = sessionStorage.getItem("fido-filters");
      if (savedFilters && savedFilters !== "{}") {
        const parsedFilters = JSON.parse(savedFilters);
        
        // If we have saved filters with a customer selected, trigger the search automatically
        if (parsedFilters.search || parsedFilters.codice_cliente || parsedFilters.order_by) {
          // Set the selectedAgent if it was saved
          if (parsedFilters.selectedAgent) {
            setSelectedAgent(parsedFilters.selectedAgent);
          }
          
          // Set orderByColumns if they were saved
          if (parsedFilters.order_by && Array.isArray(parsedFilters.order_by) && parsedFilters.order_by.length > 0) {
            setOrderByColumns(parsedFilters.order_by);
          }
          
          // Wait for formik to be ready and then trigger the search
          setTimeout(() => {
            if (formikRef.current) {
              // Update formik values with saved filters
              formikRef.current.setFieldValue("search", parsedFilters.search);
              formikRef.current.setFieldValue("agenti", parsedFilters.agenti || parsedFilters.selectedAgent);
              formikRef.current.setFieldValue("order_by", parsedFilters.order_by || []);
              
              // Trigger the search with saved filters
              filterCustomers(parsedFilters);
            }
          }, 100);
        } else {
          // No specific filters, just fetch all orders with agent IDs
          fetchOrders({});
        }
      } else {
        // No saved filters, fetch all orders with agent IDs
        fetchOrders({});
      }
      
      initialLoadDone.current = true;
    }
  }, [authorizedAgentIds]);

  useEffect(() => {
    // Start by fetching user data
    fetchUserData();
  }, []);

  const handlePageChange = (newPage: number) => {
    // Check if we can navigate to this page
    if (newPage < 0) return;
    
    // Only allow navigating to pages we've seen or the next page if hasMorePages is true
    if (newPage > highestPageSeen && (!hasMorePages || newPage > highestPageSeen + 1)) {
      return;
    }
    
    setPage(newPage);
    fetchOrders(formikRef.current?.values, newPage);
  };

  const handleSearchChange = (
    value: any,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("search", value);
  };

  const filterCustomers = async (values: any) => {
    try {
      setLoading(true);
      const apiData: any = {
        forcePageReset: true,
      };

      if (values.agenti && values.agenti.value) {
        apiData.codice_agente = [values.agenti.value];
      } else if (selectedAgent?.value) {
        apiData.codice_agente = [selectedAgent.value];
      } else {
        apiData.codice_agente = [currentAgentId];
      }

      if (values.search) {
        const searchExpanded = JSON.parse(values.search.value);
        apiData.codice_cliente = searchExpanded.codice_cliente || "";
      }

      if (
        values.order_by &&
        Array.isArray(values.order_by) &&
        values.order_by.length > 0
      ) {
        apiData.campo_ordinamento = values.order_by[0].column || "";
        apiData.tipo_ordinamento = values.order_by[0].mode || "ASC";
      }

      const filtersToSave = {
        ...values,
        selectedAgent,
        codice_agente: apiData.codice_agente,
        search: values.search, // Ensure we save the complete search object
        order_by: values.order_by || [],
        agenti: values.agenti || selectedAgent, // Save the agent selection
      };
      sessionStorage.setItem("fido-filters", JSON.stringify(filtersToSave));

      // 1. Log search action with the standard tracking system
      tracking.trackSearch('Ricerca Fido Clienti ' + new Date().getTime(), undefined, {
        filters: apiData,
        userRole,
        timestamp: new Date().toISOString()
      });
      
      // 2. Log search action with our direct method
      logSearchDirectly('fido_clienti_search', 'Ricerca Fido Clienti (Direct Log)', {
        filters: apiData,
        userRole,
        timestamp: new Date().toISOString()
      });

      setPage(0);
      setExplicit(true);
      setData([]);
      setHighestPageSeen(0); // Reset highest page seen when filtering

      // CHIAMATA FETCHORDERS
      await fetchOrders(apiData, 0);
    } catch (e) {
      console.error("Error in filterCustomers:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderByChange = async (newOrderByColumn: any) => { 
    const updatedOrderByColumns: OrderByColumn[] = [{
      column: newOrderByColumn.value,
      label: newOrderByColumn.label,
      mode: "ASC",
      opened: false,
    }];
    
    setOrderByColumns(updatedOrderByColumns);
  
    if (formikRef.current) {
      formikRef.current.setFieldValue("order_by", updatedOrderByColumns);
      formikRef.current.setFieldValue("campo_ordinamento", newOrderByColumn.value);
      formikRef.current.setFieldValue("tipo_ordinamento", "ASC"); 
    }
  };
  
  const changeOrderByItemOrder = async (orderByItem: OrderByColumn) => {
    const newMode: "ASC" | "DESC" = orderByItem.mode === "ASC" ? "DESC" : "ASC";

    const updatedOrderByColumns = [{
      ...orderByItem,
      mode: newMode
    }];
  
    setOrderByColumns(updatedOrderByColumns);
  
    if (formikRef.current) {
      formikRef.current.setFieldValue("order_by", updatedOrderByColumns);
      formikRef.current.setFieldValue("tipo_ordinamento", newMode); 
    }
  };
  
  const clearAllFilters = async (
    e: React.MouseEvent,
    setFieldValue: (field: string, value: any) => void
  ) => {
    e.preventDefault();
    setFieldValue("citta", "");
    setFieldValue("cap", "");
    setFieldValue("ritiro_cliente", "tutti");
    setFieldValue("search", "");
    setFieldValue("order_by", "");
    setFieldValue("codice_agente", "");

    // 1. Log clear filters action with standard system
/*     tracking.logAction(
      ActionCategory.USER_ACTION,
      "clear_filters",
      "User cleared all filters in Fido Clienti",
      {
        userRole,
        timestamp: new Date().toISOString()
      }
    ); */

    // 2. Log clear filters with direct method
    logSearchDirectly('fido_clienti_clear_filters', 'Reset Filtri Fido Clienti (Direct Log)', {
      userRole,
      timestamp: new Date().toISOString()
    });

    setOrderByColumns([]);
    setSelectedAgent(null);

    sessionStorage.removeItem("fido-filters");

    setPage(0);
    setExplicit(true);
    setData([]);
    setHighestPageSeen(0); // Reset highest page seen when clearing filters

    try {
      setLoading(true);
      await fetchOrders({});
    } catch (e) {
      console.error("Error clearing filters:", e);
    } finally {
      setLoading(false);
    }
  };

  // Modified Pagination component to work with hasMorePages instead of totalPages
  const Pagination = ({
    page,
    hasMorePages,
    highestPageSeen,
    theme,
  }: {
    page: number;
    hasMorePages: boolean;
    highestPageSeen: number;
    theme: string;
  }) => {
    const maxVisiblePages = 3;
    const lastKnownPage = Math.max(highestPageSeen, page);
    const hasNextPage = hasMorePages || page < lastKnownPage;

    const handlePageClick = (newPage: number) => {
      if (newPage >= 0 && (newPage <= lastKnownPage || (newPage === lastKnownPage + 1 && hasMorePages))) {
        handlePageChange(newPage);
      }
    };

    let startPage = Math.max(0, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(lastKnownPage + (hasMorePages ? 1 : 0), startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    return (
      <div className="flex justify-center items-center mt-4 mb-8">
        <button
          onClick={() => handlePageClick(0)}
          disabled={page === 0}
          className={`px-3 py-1 mx-1 ${
            page === 0
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          ««
        </button>
        <button
          onClick={() => handlePageClick(page - 1)}
          disabled={page === 0}
          className={`px-3 py-1 mx-1 ${
            page === 0
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          «
        </button>
        {Array.from(
          { length: endPage - startPage + 1 },
          (_, i) => startPage + i
        ).map((pageNumber) => (
          <button
            key={pageNumber}
            onClick={() => handlePageClick(pageNumber)}
            className={`px-3 py-1 mx-1 ${
              page === pageNumber
                ? "bg-blue-600 text-white"
                : theme === "dark"
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            {pageNumber + 1}
          </button>
        ))}
        <button
          onClick={() => handlePageClick(page + 1)}
          disabled={!hasNextPage}
          className={`px-3 py-1 mx-1 ${
            !hasNextPage
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »
        </button>
        <button
          onClick={() => handlePageClick(lastKnownPage)}
          disabled={page === lastKnownPage || !hasNextPage}
          className={`px-3 py-1 mx-1 ${
            page === lastKnownPage || !hasNextPage
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »»
        </button>
      </div>
    );
  };


  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        citta: "",
        cap: "",
        ritiro_cliente: "tutti",
        search: sessionStorage.getItem("fido-filters")
          ? JSON.parse(sessionStorage.getItem("fido-filters")!).search
          : "",
        order_by: orderByColumns,
        codice_agente: selectedAgent?.value || currentAgentId, // Assicura che codice_agente sia valorizzato
        agenti: selectedAgent || null, // Usa il valore salvato
        vista: "lista",
      }}
      validateOnChange={false}
      onSubmit={async (values) => {
        console.log("Valori passati a filterCustomers:", values);
        await filterCustomers(values);
      }}
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <Form>
          <div className="py-8 pb-24 mx-auto px-4 sm:px-6 lg:px-8">
            {loading && page === 0 ? (
              <div className="flex justify-center items-center h-screen">
                <BeatLoader color="#2563eb" />
              </div>
            ) : (
              <div className="space-y-8 px-5 sm:px-12 mx-auto flex flex-col justify-between mb-12 mt-[50px] md:mt-50">
                <h2 className="hidden sm:block text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-24 md:mt-50">
                  Fido clienti
                </h2>

                {/* Filtri */}
                <div className="filters-container flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
                  {userRole === "supervisor" && (
                    <div className="cu-wi-se-3 min-h-c border-neutral-300 dark:border-neutral-400 w-full md:w-64">
                      <Select
                        options={agentiOptions}
                        placeholder="Seleziona Agente"
                        classNames={{
                          container: () => "w-full",
                          control: () =>
                            "min-h-c border border-neutral-300 dark:border-neutral-400",
                          input: () => "!text-inherit",
                          singleValue: () =>
                            "text-neutral-900 dark:text-neutral-100",
                        }}
                        value={values.agenti} // Usa il valore di Formik
                        onChange={async (val) => {
                          setFieldValue("agenti", val); // Aggiorna Formik
                          setSelectedAgent(val); // Aggiorna anche lo stato di selectedAgent
                          setFieldValue("search", null);
                          const agentCodes = val
                            ? processAgentCodeValue(val.value)
                            : authorizedAgentIds;
                          if (agentCodes.length > 0) {
                            const clients = await fetchClientsByAgentCodes(agentCodes);
                            if (Array.isArray(clients)) {
                              setFilters({
                                searchFilters: clients.map((item: any) => ({
                                  codice_cliente: item.CODICE_CLIENTE,
                                  ragione_sociale: item.RAGIONE_SOCIALE,
                                })),
                              });
                            }
                          }
                        }}
                        isClearable
                      />
                    </div>
                  )}

                  <div className="w-full md:w-auto">
                    <Select
                      classNames={{
                        container: (state: any) => `w-full md:w-auto`,
                        control: (state: any) =>
                          `min-h-c cu-wi-se border border-neutral-300 dark:border-neutral-400`,
                        singleValue: (state: any) =>
                          `!text-neutral-500 dark:!text-neutral-400`,
                        option: (state: any) =>
                          `hover:bg-blue-500 hover:text-white`,
                      }}
                      onChange={(val: any) =>
                        handleSearchChange(val, setFieldValue)
                      }
                      value={values.search}
                      isClearable={true}
                      placeholder="Cliente / Codice Cliente"
                      options={filters?.searchFilters?.map(
                        (sf: any, i: number) => ({
                          value: JSON.stringify(sf),
                          label: `${sf.ragione_sociale} (${sf.codice_cliente})`,
                        })
                      )}
                    />
                  </div>

                  {/* Ordina per */}
                  <div className="w-full md:w-auto">
                    {values.order_by &&
                      Array.isArray(values.order_by) &&
                      values.order_by.map((orderByItem: OrderByColumn) => (
                        <div
                          key={orderByItem.column}
                          className={classNames({
                            "grid grid-cols-4 border": true,
                            "pointer-events-none opacity-50": !!values.search,
                          })}
                        >
                          <div className="col-span-4 px-3 py-1.5 flex items-center justify-between">
                            <h4>{orderByItem.label}</h4>
                            <div className="flex">
                              <span
                                className={classNames({
                                  "material-symbols-outlined cursor-pointer text-xl":
                                    true,
                                  "text-neutral-300 dark:text-natural-600":
                                    orderByItem.mode !== "DESC",
                                  "text-blue-500 dark:text-blue-400":
                                    orderByItem.mode === "DESC",
                                })}
                                onClick={() =>
                                  changeOrderByItemOrder(orderByItem)
                                }
                              >
                                arrow_upward
                              </span>
                              <span
                                className={classNames({
                                  "material-symbols-outlined cursor-pointer text-xl":
                                    true,
                                  "text-neutral-300 dark:text-natural-600":
                                    orderByItem.mode !== "ASC",
                                  "text-blue-500 dark:text-blue-400":
                                    orderByItem.mode === "ASC",
                                })}
                                onClick={() =>
                                  changeOrderByItemOrder(orderByItem)
                                }
                              >
                                arrow_downward
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    <Select
                      onChange={(val: any) => handleOrderByChange(val)}
                      value={values.order_by?.[0]?.column || ""}
                      placeholder="+ Ordinamento"
                      options={columns.map(c => ({ value: c, label: c }))}
                      classNames={{
                        container: (state: any) => `cu-wi-se w-full md:w-auto`,
                        control: (state: any) =>
                          `min-h-c cu-wi-se border border-neutral-300 dark:border-neutral-400`,
                        singleValue: (state: any) =>
                          `!text-neutral-500 dark:!text-neutral-400`,
                        option: (state: any) =>
                          `hover:bg-blue-500 hover:text-white`,
                      }}
                    />
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={(e) => clearAllFilters(e, setFieldValue)}
                      className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700"
                    >
                      Cancella filtri
                    </button>
                    <button
                      type="submit"
                      className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700"
                    >
                      Filtra
                    </button>
                  </div>
                </div>

                {/* Tabella */}
                <div
                  className="relative table-container custom-scrollbar max-h-[calc(100vh-300px)]"
                  ref={tableRef}
                >
                  <table className="w-full min-w-max">
                    <thead>
                      <tr>
                        <th>Codice cliente</th>
                        <th>Dettaglio</th>
                        <th>Ragione sociale</th>
                        <th>Fido</th>
                        <th>Saldo Contabile</th>
                        <th>Esposizione RiBa</th>
                        <th>Titoli</th>
                        <th>Bolle in attesa di fattura</th>
                        <th>Cambiale</th>
                        <th>Ordini programmati</th>
                        <th>Ordini</th>
                        <th>Insoluti</th>
                        <th>Altro</th>
                        <th>Eventuale Fido Assicurato</th> 
                        <th>Fido utilizzato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((s: any) => (
                        <tr key={s.CODICE_CLIENTE}>
                          <td>{s.CODICE_CLIENTE}</td>
                          <td>
                            <button
                              onClick={() =>
                                navigate(
                                  `/fido-clienti/${s.CODICE_CLIENTE}/detail`
                                )
                              }
                              className="px-2 py-1 text-white rounded focus:outline-none focus:ring-2 focus:ring-opacity-50"
                            >
                              &#128269;
                            </button>
                          </td>
                          <td>{s.RAGIONE_SOCIALE}</td>
                          <td>
                            {Number(s.FIDO || 0).toLocaleString("it-IT", 
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.SALDO_CONTABILE || 0).toLocaleString(
                              "it-IT",
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.ESPOSIZIONE_RIBA || 0).toLocaleString(
                              "it-IT",
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.TITOLI || 0).toLocaleString("it-IT", 
                            {
                              
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                            })}
                          </td>
                          <td>
                            {Number(
                              s.BOLLE_IN_ATTESA_DI_FATTURA || 0
                            ).toLocaleString("it-IT", 
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.CAMBIALE || 0).toLocaleString(
                              "it-IT",
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.ORDINI_PROGRAMMATI || 0).toLocaleString(
                              "it-IT",
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.ORDINI || 0).toLocaleString("it-IT", 
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.INSOLUTI || 0).toLocaleString("it-IT", 
                            { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              })}
                          </td>
                          
                            {/* <td>
                            {Number(s.TITOLI_A_RISCHIO || 0).toLocaleString(
                              "it-IT",
                              { style: "currency", currency: "EUR" }
                            )}
                          </td>
                          <td>
                            {Number(s.CAMBIALE || 0).toLocaleString("it-IT", {
                              style: "currency",
                              currency: "EUR",
                            })}
                          </td> */}
                          <td>
                            {Number(s.ALTRO || 0).toLocaleString("it-IT", 
                                 { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.FIDO_ASSICURATO || 0).toLocaleString("it-IT",
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                          <td>
                            {Number(s.SALDO_CONTABILE + s.BOLLE_IN_ATTESA_DI_FATTURA + s.ORDINI + s.TITOLI + s.ESPOSIZIONE_RIBA + s.CAMBIALE || 0).toLocaleString("it-IT", 
                              { 
                                    style: "currency",
                                    currency: "EUR",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0 
                              }
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginazione */}
                <Pagination 
                  page={page} 
                  hasMorePages={hasMorePages} 
                  highestPageSeen={highestPageSeen} 
                  theme={theme} 
                />
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}
